import { z } from "zod"

import { paginationResponseSchema } from "@/shared/api/types"
import {
	departmentSchema,
	ecologicalZoningSchema,
	ruleSchema
} from "@/shared/store/referential/referential"

export const DISPLAY_PREVIEW_ZOOM_LEVEL = 13

export const CLEAR_CUTTING_STATUSES = [
	"to_validate",
	"in_progress",
	"waiting_for_validation",
	"validated",
	"legal_validated",
	"final_validated",
	"rejected"
] as const

export const clearCutStatusSchema = z.enum(CLEAR_CUTTING_STATUSES)

export type ClearCutStatus = z.infer<typeof clearCutStatusSchema>

const geoJsonTypeSchema = z.enum(["Point", "MultiPolygon"])
const pointSchema = z.object({
	type: geoJsonTypeSchema.extract(["Point"]),
	coordinates: z.tuple([z.number(), z.number()])
})
const multiPolygonSchema = z.object({
	type: geoJsonTypeSchema.extract(["MultiPolygon"]),
	coordinates: z.array(z.array(z.array(z.tuple([z.number(), z.number()]))))
})

export type Point = z.infer<typeof pointSchema>
export type MultiPolygon = z.infer<typeof multiPolygonSchema>

export const clearCutResponseSchema = z.object({
	id: z.string(),
	boundary: multiPolygonSchema,
	location: pointSchema,
	observationStartDate: z.iso.date(),
	observationEndDate: z.iso.date(),
	ecologicalZoningIds: z.string().array(),
	areaHectare: z.number(),
	isManuallyEdited: z.boolean().optional(),
	manuallyEditedAt: z.iso.date().optional().nullable(),
	allowPipelineOverride: z.boolean().optional()
})
export type ClearCutResponse = z.infer<typeof clearCutResponseSchema>
const clearCutSchema = clearCutResponseSchema
	.omit({
		ecologicalZoningIds: true
	})
	.extend(
		z.object({
			ecologicalZonings: ecologicalZoningSchema.array()
		}).shape
	)
export type ClearCut = z.infer<typeof clearCutSchema>

export const publicUserSchema = z.object({
	login: z.string(),
	email: z.email()
})
export type PublicUser = z.infer<typeof publicUserSchema>
export const clearCutReportResponseSchema = z.object({
	id: z.string(),
	clearCuts: z.array(clearCutResponseSchema),
	city: z.string(),
	comment: z.string().optional(),
	name: z.string().optional(),
	status: clearCutStatusSchema,
	averageLocation: pointSchema,
	slopeAreaHectare: z.number().optional(),
	departmentId: z.string(),
	createdAt: z.iso.date(),
	updatedAt: z.iso.date(),
	reportedAt: z.iso.date().optional().nullable(),
	isManuallyEdited: z.boolean().optional(),
	manuallyEditedAt: z.iso.date().optional().nullable(),
	allowPipelineOverride: z.boolean().optional(),
	totalAreaHectare: z.number(),
	totalBdfResinousAreaHectare: z.number().optional(),
	totalBdfDeciduousAreaHectare: z.number().optional(),
	totalBdfMixedAreaHectare: z.number().optional(),
	totalBdfPoplarAreaHectare: z.number().optional(),
	lastCutDate: z.iso.date(),
	firstCutDate: z.iso.date(),
	satelliteImages: z.array(z.url()).optional(),
	rulesIds: z.array(z.string()),
	userId: z.string().optional().nullable(),
	affectedUser: publicUserSchema.optional().nullable(),
	assignmentRequestedById: z.string().optional().nullable(),
	assignmentRequestedBy: publicUserSchema.optional().nullable()
})
export type ClearCutReportResponse = z.infer<
	typeof clearCutReportResponseSchema
>

export const clearCutReportSchema = clearCutReportResponseSchema
	.omit({ rulesIds: true, clearCuts: true, departmentId: true })
	.extend(
		z.object({
			department: departmentSchema,
			rules: ruleSchema.array(),
			clearCuts: z.array(clearCutSchema)
		}).shape
	)

export type ClearCutReport = z.infer<typeof clearCutReportSchema>

const countedPoint = z.object({
	count: z.number(),
	point: pointSchema,
	/** [west, south, east, north] of a cluster's points */
	bounds: z.tuple([z.number(), z.number(), z.number(), z.number()]).nullish()
})
export type CountedPoint = z.infer<typeof countedPoint>
const clusterizedPointsSchema = z.object({
	total: z.number(),
	content: countedPoint.array()
})
export const clearCutsResponseSchema = z.object({
	points: clusterizedPointsSchema,
	previews: z.array(clearCutReportResponseSchema)
})

export type ClearCutsResponse = z.infer<typeof clearCutsResponseSchema>

export const myAssignedReportsResponseSchema = paginationResponseSchema(
	clearCutReportResponseSchema
)
export type MyAssignedReportsResponse = z.infer<
	typeof myAssignedReportsResponseSchema
>

const clearCutsSchema = clearCutsResponseSchema.omit({ previews: true }).extend(
	z.object({
		previews: z.array(clearCutReportSchema)
	}).shape
)
export type ClearCuts = z.infer<typeof clearCutsSchema>

const clearCutFormGroundSchema = z.object({
	inspectionDate: z.iso.datetime({ local: true }).optional(),
	weather: z.string().prefault(""),
	forest: z.string().prefault(""),
	hasRemainingTrees: z.boolean().prefault(false),
	treesSpecies: z.string().prefault(""),
	plantingImages: z.array(z.string()).prefault([]),
	hasConstructionPanel: z.boolean().prefault(false),
	constructionPanelImages: z.array(z.string()).optional(),
	wetland: z.string().prefault(""),
	destructionClues: z.string().prefault(""),
	soilState: z.string().prefault(""),
	clearCutImages: z.array(z.string()).prefault([]),
	treeTrunksImages: z.array(z.string()).prefault([]),
	soilStateImages: z.array(z.string()).prefault([]),
	accessRoadImages: z.array(z.string()).prefault([])
})

const clearCutFormEcologicalZoningSchema = z.object({
	hasOtherEcologicalZone: z.boolean().prefault(false),
	otherEcologicalZoneType: z.string().prefault(""),
	hasNearbyEcologicalZone: z.boolean().prefault(false),
	nearbyEcologicalZoneType: z.string().prefault(""),
	protectedSpecies: z.string().prefault(""),
	protectedHabitats: z.string().prefault(""),
	hasDdtRequest: z.boolean().prefault(false),
	ddtRequestOwner: z.string().prefault("")
})

const clearCutFormActorsSchema = z.object({
	company: z.string().prefault(""),
	subcontractor: z.string().prefault(""),
	landlord: z.string().prefault("")
})

const clearCutFormRegulationSchema = z.object({
	isPefcFscCertified: z.boolean().nullable().prefault(null),
	isOver20Ha: z.boolean().nullable().prefault(null),
	isPsgRequiredPlot: z.boolean().nullable().prefault(null)
})

const clearCutFormLegalStrategySchema = z.object({
	relevantForPefcComplaint: z.boolean().prefault(false),
	relevantForRediiiComplaint: z.boolean().prefault(false),
	relevantForOfbComplaint: z.boolean().prefault(false),
	relevantForAlertCnpfDdtSrgs: z.boolean().prefault(false),
	relevantForAlertCnpfDdtPsgThresholds: z.boolean().prefault(false),
	relevantForPsgRequest: z.boolean().prefault(false),
	requestEngaged: z.string().prefault("")
})

const clearCutFormOtherSchema = z.object({
	other: z.string().optional()
})

const existingClearCutFormSchema = z.object({
	id: z.string(),
	reportId: z.string(),
	createdAt: z.iso.datetime({ local: true }),
	etag: z.string()
})
const clearCutFormSectionsResponseSchema = clearCutFormOtherSchema
	.extend(clearCutFormGroundSchema.shape)
	.extend(clearCutFormEcologicalZoningSchema.shape)
	.extend(clearCutFormActorsSchema.shape)
	.extend(clearCutFormRegulationSchema.shape)
	.extend(clearCutFormLegalStrategySchema.shape)

export const clearCutFormResponseSchema = existingClearCutFormSchema.extend(
	clearCutFormSectionsResponseSchema.shape
)

export type ClearCutFormResponse = z.infer<typeof clearCutFormResponseSchema>

export const clearCutFormSchema = clearCutFormSectionsResponseSchema.extend(
	z
		.object({
			report: clearCutReportSchema,
			ecologicalZonings: ecologicalZoningSchema.array().prefault([]),
			hasEcologicalZonings: z.boolean().prefault(false)
		})
		.extend(
			existingClearCutFormSchema
				.omit({ etag: true, id: true, createdAt: true })
				.extend(
					z.object({
						etag: existingClearCutFormSchema.shape.etag.optional(),
						id: existingClearCutFormSchema.shape.id.optional(),
						createdAt: existingClearCutFormSchema.shape.createdAt.optional()
					}).shape
				).shape
		).shape
)

export const clearCutFormVersionsSchema = z.object({
	latest: clearCutFormSchema.optional(),
	original: clearCutFormSchema,
	current: clearCutFormSchema,
	versionMismatchDisclaimerShown: z.boolean()
})
export type ClearCutFormVersions = z.infer<typeof clearCutFormVersionsSchema>
export type ClearCutForm = z.infer<typeof clearCutFormSchema>
export type ClearCutFormInput = z.input<typeof clearCutFormSchema>
export const clearCutFormsResponseSchema = paginationResponseSchema(
	clearCutFormResponseSchema
)

export type ClearCutFormsResponse = z.infer<typeof clearCutFormsResponseSchema>

export const clearCutFormCreateSchema = clearCutFormResponseSchema.omit({
	id: true,
	createdAt: true,
	etag: true,
	reportId: true
})

export type ClearCutFormCreate = z.infer<typeof clearCutFormCreateSchema>
